import { useContext, useState } from "react";
import { MapPin, Loader2, ZoomIn, ZoomOut, Maximize2, Building2, Map, ExternalLink } from "lucide-react";
import { DataContext } from "@/App";
import { MapLegend } from "@/components/MapLegend";
import { StadiumMapLegend } from "@/components/StadiumMapLegend";
import { OConnellMapLegend } from "@/components/OConnellMapLegend";
import { OConnellCenterInteractiveMap } from "@/components/OConnellCenterInteractiveMap";
import { BenHillGriffinInteractiveMap } from "@/components/BenHillGriffinInteractiveMap";
import { QuietRoomCard } from "@/components/QuietRoomCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OCONNELL_CENTER_DATA, getAllOConnellLocations } from "@/data/oconnellCenterData";
import { BEN_HILL_GRIFFIN_DATA, getAllStadiumLocations } from "@/data/benHillGriffinData";

const VENUES = {
  stadium: {
    id: "stadium",
    name: "Ben Hill Griffin Stadium",
    shortName: "The Swamp",
    mapImage: "https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/ffn2hzts_GameDayUF%20Legend%20Map.png",
    hasStaticMap: true,
    data: BEN_HILL_GRIFFIN_DATA,
  },
  oconnell: {
    id: "oconnell",
    name: "O'Connell Center",
    shortName: "The O'Dome",
    hasStaticMap: false,
    data: OCONNELL_CENTER_DATA,
  }
};

export default function MapView() {
  const { locations, loading } = useContext(DataContext);
  const [selectedVenue, setSelectedVenue] = useState("stadium");
  const [showQuietRooms, setShowQuietRooms] = useState(false);
  const [showFullMap, setShowFullMap] = useState(false);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);
  const [mapZoom, setMapZoom] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const venue = VENUES[selectedVenue];
  
  // Get quiet rooms based on venue
  const getQuietRooms = () => {
    if (selectedVenue === "stadium") {
      // Use stadium data
      return BEN_HILL_GRIFFIN_DATA.sensoryRooms.map(room => ({
        ...room,
        type: "quiet_zone"
      }));
    } else {
      return getAllOConnellLocations().filter(loc => loc.type === "quiet_zone");
    }
  };

  // Get all locations for venue
  const getVenueLocations = () => {
    let allLocations;
    if (selectedVenue === "stadium") {
      allLocations = getAllStadiumLocations();
    } else {
      allLocations = getAllOConnellLocations();
    }
    
    if (selectedCategory === "all") return allLocations;
    return allLocations.filter(loc => {
      if (selectedCategory === "quiet") return loc.type === "quiet_zone";
      if (selectedCategory === "seating") return loc.type === "accessible_section" || loc.type === "sensory_friendly";
      if (selectedCategory === "services") return loc.type === "guest_services" || loc.type === "medical" || loc.type === "family_services";
      if (selectedCategory === "exits") return loc.type === "emergency_exit";
      if (selectedCategory === "restrooms") return loc.type === "restroom";
      return true;
    });
  };

  const quietRooms = getQuietRooms();

  const handleNavigate = (location) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
    window.open(url, "_blank");
    toast.success(`Opening directions to ${location.name}`);
  };

  const handleZoomIn = () => {
    setMapZoom((prev) => Math.min(prev + 0.25, 2));
  };

  const handleZoomOut = () => {
    setMapZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe" data-testid="map-view">
      {/* Header with Logo and Venue Selector */}
      <header className="mb-4">
        <div className="flex items-center gap-4 mb-3">
          <img
            src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
            alt="XTERMIGATOR Kids Logo"
            className="w-20 h-20 object-contain"
          />
          <div className="flex-1">
            <h1
              className="text-xl font-bold text-foreground"
              style={{ fontFamily: "Lexend, sans-serif" }}
            >
              Sensory Resource Map
            </h1>
            <p className="text-xs text-muted-foreground">XTERMIGATOR Kids Inclusive Game Day</p>
          </div>
        </div>
        
        {/* Venue Selector */}
        <Select value={selectedVenue} onValueChange={setSelectedVenue}>
          <SelectTrigger className="w-full h-12" data-testid="venue-selector">
            <Building2 size={18} className="mr-2 text-primary" />
            <SelectValue placeholder="Select Venue" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="stadium">
              <div className="flex items-center gap-2">
                <span className="font-medium">Ben Hill Griffin Stadium</span>
                <span className="text-xs text-muted-foreground">(The Swamp)</span>
              </div>
            </SelectItem>
            <SelectItem value="oconnell">
              <div className="flex items-center gap-2">
                <span className="font-medium">O'Connell Center</span>
                <span className="text-xs text-muted-foreground">(The O'Dome)</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </header>

      {/* Ben Hill Griffin Stadium */}
      {selectedVenue === "stadium" && (
        <>
          {/* Interactive Map Button */}
          <Button
            variant="default"
            className="w-full h-12 rounded-full mb-4 bg-gradient-to-r from-[#0021A5] to-[#FA4616]"
            onClick={() => setShowInteractiveMap(true)}
            data-testid="open-stadium-interactive-map"
          >
            <Map size={18} className="mr-2" />
            Open Interactive Floor Plan
          </Button>

          {/* Static Map Image */}
          <div
            className="relative rounded-2xl overflow-hidden border-2 border-border shadow-lg bg-white"
            data-testid="stadium-map"
          >
            <div
              className="overflow-auto"
              style={{
                maxHeight: "calc(100vh - 450px)",
                minHeight: "250px",
              }}
            >
              <img
                src={venue.mapImage}
                alt="Ben Hill Griffin Stadium Accessibility & Sensory Resource Map"
                className="w-full h-auto"
                style={{
                  transform: `scale(${mapZoom})`,
                  transformOrigin: "top left",
                  transition: "transform 0.2s ease-out",
                }}
              />
            </div>

            {/* Map Controls */}
            <div className="absolute top-3 right-3 flex flex-col gap-2">
              <Button
                variant="secondary"
                size="icon"
                className="w-10 h-10 rounded-full shadow-md bg-white/90 hover:bg-white"
                onClick={handleZoomIn}
                aria-label="Zoom in"
              >
                <ZoomIn size={20} />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="w-10 h-10 rounded-full shadow-md bg-white/90 hover:bg-white"
                onClick={handleZoomOut}
                aria-label="Zoom out"
              >
                <ZoomOut size={20} />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="w-10 h-10 rounded-full shadow-md bg-white/90 hover:bg-white"
                onClick={() => setShowFullMap(true)}
                aria-label="View full map"
              >
                <Maximize2 size={20} />
              </Button>
            </div>
          </div>

          {/* Category Filter for Stadium */}
          <div className="mt-4 mb-4">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full h-11">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accessibility Features</SelectItem>
                <SelectItem value="quiet">Sensory Rooms & Quiet Spaces</SelectItem>
                <SelectItem value="seating">Accessible & Sensory Seating</SelectItem>
                <SelectItem value="services">Guest & Medical Services</SelectItem>
                <SelectItem value="exits">Emergency Exits / Gates</SelectItem>
                <SelectItem value="restrooms">Accessible Restrooms</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stadium Legend */}
          <StadiumMapLegend />

          {/* Stadium Locations List */}
          <div className="mt-4 space-y-3">
            <h2 className="section-header text-lg" style={{ fontFamily: "Lexend, sans-serif" }}>
              Accessibility Locations
            </h2>
            {getVenueLocations().map((location) => (
              <LocationCard 
                key={location.id} 
                location={location}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        </>
      )}

      {/* O'Connell Center */}
      {selectedVenue === "oconnell" && (
        <>
          {/* Interactive Map Button */}
          <Button
            variant="default"
            className="w-full h-12 rounded-full mb-4 bg-gradient-to-r from-[#0021A5] to-[#FA4616]"
            onClick={() => setShowInteractiveMap(true)}
            data-testid="open-interactive-map"
          >
            <Map size={18} className="mr-2" />
            Open Interactive Floor Plan
          </Button>

          {/* Category Filter */}
          <div className="mb-4">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full h-11">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accessibility Features</SelectItem>
                <SelectItem value="quiet">Quiet Zones & Sensory Rooms</SelectItem>
                <SelectItem value="seating">Accessible & Sensory Seating</SelectItem>
                <SelectItem value="services">Guest & Medical Services</SelectItem>
                <SelectItem value="exits">Emergency Exits</SelectItem>
                <SelectItem value="restrooms">Accessible Restrooms</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* O'Connell Center Legend */}
          <OConnellMapLegend />

          {/* Locations List */}
          <div className="mt-4 space-y-3">
            <h2 className="section-header text-lg" style={{ fontFamily: "Lexend, sans-serif" }}>
              Accessibility Locations
            </h2>
            {getVenueLocations().map((location) => (
              <LocationCard 
                key={location.id} 
                location={location}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        </>
      )}

      {/* Quiet Room Locator Toggle */}
      <div className="mt-4">
        <Button
          variant={showQuietRooms ? "default" : "outline"}
          className="w-full h-12 rounded-full"
          onClick={() => setShowQuietRooms(!showQuietRooms)}
          data-testid="toggle-quiet-rooms"
        >
          <MapPin size={18} className="mr-2" />
          {showQuietRooms ? "Hide Quiet Retreats" : "Find Quiet Retreats"}
        </Button>
      </div>

      {/* Quiet Rooms List */}
      {showQuietRooms && (
        <section className="mt-4" aria-label="Quiet retreat locations">
          <h2
            className="section-header"
            style={{ fontFamily: "Lexend, sans-serif" }}
          >
            Sensory Rooms & Quiet Retreats
          </h2>

          <div className="space-y-4">
            {quietRooms.length > 0 ? (
              quietRooms.map((room) => (
                <QuietRoomCard
                  key={room.id}
                  location={room}
                  onNavigate={handleNavigate}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No quiet rooms available at this venue
              </p>
            )}
          </div>
        </section>
      )}

      {/* Website Link */}
      <div className="mt-6">
        <a
          href="https://www.inclusivegameday.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-secondary hover:bg-secondary/80 transition-colors text-foreground font-medium"
          data-testid="website-link-map"
        >
          <ExternalLink size={18} />
          Visit inclusivegameday.com
        </a>
      </div>

      {/* Full Map Dialog (Stadium static image) */}
      {selectedVenue === "stadium" && (
        <Dialog open={showFullMap} onOpenChange={setShowFullMap}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto p-0">
            <DialogHeader className="p-4 border-b">
              <DialogTitle style={{ fontFamily: "Lexend, sans-serif" }}>
                Ben Hill Griffin Stadium - Accessibility & Sensory Resource Map
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto">
              <img
                src={venue.mapImage}
                alt="Full Stadium Map"
                className="w-full h-auto"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Interactive Map Modals */}
      {showInteractiveMap && selectedVenue === "oconnell" && (
        <OConnellCenterInteractiveMap
          onLocationSelect={(zoneId) => {
            toast.success(`Selected: ${zoneId}`);
          }}
          onClose={() => setShowInteractiveMap(false)}
        />
      )}

      {showInteractiveMap && selectedVenue === "stadium" && (
        <BenHillGriffinInteractiveMap
          onLocationSelect={(zoneId) => {
            toast.success(`Selected: ${zoneId}`);
          }}
          onClose={() => setShowInteractiveMap(false)}
        />
      )}
    </div>
  );
}

// Location Card Component
function LocationCard({ location, onNavigate }) {
  const getTypeIcon = (type) => {
    const icons = {
      quiet_zone: "🤫",
      accessible_section: "♿",
      sensory_friendly: "🎧",
      guest_services: "ℹ️",
      medical: "🏥",
      family_services: "👨‍👩‍👧",
      emergency_exit: "🚪",
      restroom: "🚻"
    };
    return icons[type] || "📍";
  };

  const getEnergyColor = (level) => {
    const colors = {
      low: { bg: "bg-green-100", text: "text-green-700", label: "Quiet" },
      medium: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Moderate" },
      high: { bg: "bg-red-100", text: "text-red-700", label: "Loud" }
    };
    return colors[level] || colors.medium;
  };

  const energyStyle = getEnergyColor(location.energy_level);

  // Format amenities for display
  const formatAmenity = (amenity) => {
    if (typeof amenity === 'string') {
      return amenity.replace(/_/g, " ");
    }
    return amenity;
  };

  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">
          {getTypeIcon(location.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-base" style={{ fontFamily: "Lexend, sans-serif" }}>
              {location.name}
            </h3>
            <span className={`text-xs px-2 py-1 rounded-full ${energyStyle.bg} ${energyStyle.text}`}>
              {energyStyle.label}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {location.description}
          </p>
          
          {/* Access Instructions */}
          {location.access_instructions && (
            <p className="text-xs text-primary mt-2">
              📍 {location.access_instructions}
            </p>
          )}
          
          {/* Capacity (if available) */}
          {location.capacity && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Capacity</span>
                <span>{location.current_occupancy || 0}/{location.capacity}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full"
                  style={{
                    width: `${((location.current_occupancy || 0) / location.capacity) * 100}%`,
                    backgroundColor: energyStyle.text.includes('green') ? '#34D399' : 
                                     energyStyle.text.includes('yellow') ? '#FBBF24' : '#F87171'
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Amenities */}
          {location.amenities && location.amenities.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {location.amenities.slice(0, 4).map((amenity) => (
                <span
                  key={amenity}
                  className="text-xs bg-secondary px-2 py-0.5 rounded-full"
                >
                  {formatAmenity(amenity)}
                </span>
              ))}
              {location.amenities.length > 4 && (
                <span className="text-xs text-muted-foreground">
                  +{location.amenities.length - 4} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Navigate Button */}
      <Button
        variant="outline"
        className="w-full mt-3 h-10 rounded-full"
        onClick={() => onNavigate(location)}
      >
        <MapPin size={16} className="mr-2" />
        Get Directions
      </Button>
    </div>
  );
}
